import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ScrollingModule } from '@angular/cdk/scrolling';

import { catchError, debounceTime, forkJoin, from, lastValueFrom, map, of, Subscription, switchMap, take, tap } from 'rxjs';
import { AlertService } from '../shared/services/alert.service';
import { DeviceService } from '../shared/services/device.service';
import { ImageService } from '../shared/services/img-base64.service';
import { IndexedDbPollService } from '../shared/services/indexeddb-poll.service';
import { IndexedDbResponseSendService } from '../shared/services/indexeddb-response-send.service';
import { IndexedDbUnitService } from '../shared/services/indexeddb-unit.service';
import { PollService } from '../shared/services/poll.service';
import { UnitService } from '../shared/services/unit.service';
import { LoadingService } from '../shared/services/loading.service';
import { IncentiveService } from "../shared/services/incentive.service";
import { IndexedDbIncentiveService } from "../shared/services/indexeddb-incentive.service";
import { PollTypeEnum } from "../shared/enums/poll-type.enum";
import { IPoll } from 'projects/lib-ui/src/shared/interfaces/poll';
import { HeaderComponent } from 'projects/lib-ui/src/shared/components/header/header.component';
import { IAvailableDevices, IPartialUnit } from 'projects/lib-ui/src/shared/interfaces/unit';
import { INCENTIVE_IMG_DEFAULT } from 'projects/lib-ui/src/shared/constants/incentive-img-default.const';
import { IIncentive } from 'projects/lib-ui/src/shared/interfaces/incentive';

@Component({
  selector: 'app-initial-setup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HeaderComponent,
    ScrollingModule
  ],
  providers: [UnitService, DeviceService],
  templateUrl: './initial-setup.component.html',
  styleUrls: ['./initial-setup.component.scss'],
})
export class InitialSetupComponent implements OnInit, OnDestroy {
  private readonly indexedDbUnitService = inject(IndexedDbUnitService);
  private readonly router = inject(Router);
  private readonly pollService = inject(PollService);
  private readonly indexedDbPollService = inject(IndexedDbPollService);
  private readonly loadingService = inject(LoadingService);
  private readonly imageService = inject(ImageService);
  private readonly unitService = inject(UnitService);
  private readonly deviceService = inject(DeviceService);
  private readonly alertService = inject(AlertService);
  private readonly indexedDbResponseSendService = inject(
    IndexedDbResponseSendService
  );
  private readonly indexedDbIncentiveService = inject(IndexedDbIncentiveService);
  private readonly incentiveService = inject(IncentiveService);

  private readonly subs: Subscription[] = [];

  loading = false;
  searched = false;
  search = new FormControl('');
  unitList: IPartialUnit[] = [];

  unitSelected: IPartialUnit | null = null;
  deviceSelected: IAvailableDevices | null = null;
  latitude: string | null = null;
  longitude: string | null = null;

  ngOnInit(): void {
    this.subs.push(
      this.search.valueChanges
        .pipe(debounceTime(200))
        .subscribe(() => (this.searched = false))
    );

    this.setPosition();

    this.indexedDbResponseSendService.clearDB().then(() => {
    });
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  searchUnits(): void {
    if (this.search.value) {
      this.setPosition();
      this.loading = true;
      this.unitSelected = null;
      this.deviceSelected = null;

      this.subs.push(
        this.unitService.getUnits(this.search.value).subscribe((response) => {
          if (!response || !response.success || !response.data) {
            this.alertService.info('Nenhuma unidade encontrada');
            this.unitList = [];
            this.loading = false;
            return;
          }
          const data = response.data.map((unit) => {
            return {
              ...unit,
              availableDevices: !unit.availableDevices ? [] : unit.availableDevices.sort((a, b) =>
                a.typePoll.toString().localeCompare(b.typePoll.toString()),
              )
            }
          });
          this.searched = true;
          this.unitList = data;
          this.loading = false;
        })
      );
    } else {
      this.unitList = [];
    }
  }

  selectUnit(unit: IPartialUnit): void {
    if (!unit.pollId && !unit.pollDishReservationId) {
      this.alertService.info(`Não há pesquisa/reserva vinculada a esta unidade`);
      this.unitSelected = null;
      this.deviceSelected = null;
    } else {
      this.unitSelected = unit;
      this.deviceSelected = null;
    }

    this.setPosition();
  }

  configDone() {
    this.loadingService.showLoading();
    const data: IPartialUnit = {
      ...this.unitSelected!,
      device: this.deviceSelected!,
      availableDevices: [],
    };

    this.linkDevice(data);
  }

  linkDevice(data: IPartialUnit): void {

    this.deviceService
      .linkDevice(data.device.id, this.latitude ?? '', this.longitude ?? '')
      .pipe(
        take(1),
        tap(({ success }) => {
          if (success) {
            this.addUnitToIndexedDb(data);
          }
        })
      )
      .subscribe();
  }

  addUnitToIndexedDb(data: IPartialUnit): void {
    this.indexedDbUnitService.addUnit(data).then(() => {
      this.getPoll(data);
    });
  }

  getPoll(dataUnit: IPartialUnit): void {
    this.pollService
      .getPollById(
        dataUnit.device.typePoll === PollTypeEnum.Satisfaction
          ? dataUnit.pollId
          : dataUnit.pollDishReservationId
      ).pipe(
        take(1),
        tap(({ success, data }) => {
          if (success) {

            if (data) {
              this.convertImgsToBase64(dataUnit, data);
            }

          }
        })
      )
      .subscribe();
  }



  async convertImgsToBase64(
    dataUnit: IPartialUnit,
    dataPoll: IPoll
  ): Promise<void> {
    this.imageService
      .convertImgsURLToBase64(dataPoll)
      .then((res) => this.createPoll(dataUnit, res));
  }

  createPoll(dataUnit: IPartialUnit, dataPoll: IPoll): void {
    from(this.indexedDbPollService.addPoll(dataPoll)).pipe(
      switchMap(() =>
        this.incentiveService.getBranchById(dataUnit.device.branchId).pipe(
          take(1),
          switchMap(incentive => {
            if (!incentive) return of(null);
            return from(this.indexedDbIncentiveService.clearDB()).pipe(
              catchError(() => of(null)),
              map(() => incentive)
            );
          }),
          switchMap(incentive => {
            if (!incentive) return of(null);
            const todayDate = new Date().toISOString().split('T')[0];
            const startDate = incentive.startDate.split('T')[0];
            const endDate = incentive.endDate.split('T')[0];
            if (startDate > todayDate || endDate < todayDate) return of(null);
            return forkJoin({
              noticePageImg: this.imageService.convertToBase64(incentive.noticePageImg).pipe(catchError(() => of(''))),
              winnerPageImg: this.imageService.convertToBase64(incentive.winnerPageImg).pipe(catchError(() => of('')))
            }).pipe(
              map(({ noticePageImg, winnerPageImg }) => ({
                ...incentive,
                noticePageImg: noticePageImg || INCENTIVE_IMG_DEFAULT,
                winnerPageImg: winnerPageImg || INCENTIVE_IMG_DEFAULT,
              }))
            );
          }),
          tap(incentiveData => {
            if (incentiveData) {
              this.addIncentiveToIndexedDb(incentiveData);
            }
          })
        )
      )
    ).subscribe(() => {
      this.router.navigate([
        `../unit/${dataUnit.id}/device/${dataUnit.device.id}/poll/${dataUnit.device.typePoll === PollTypeEnum.Satisfaction ? dataUnit.pollId : dataUnit.pollDishReservationId}`
      ]);
    });
  }

  setPosition(): void {
    this.getCurrentPosition().then((position) => {
      this.latitude = position.coords.latitude.toString();
      this.longitude = position.coords.longitude.toString();
    });
  }

  getCurrentPosition(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      }
    });
  }

  trackByIndex(index: number): number {
    return index;
  }

  private getIncentive(branchId: string): void {
    this.incentiveService.getBranchById(branchId).pipe(
      take(1),
      switchMap(incentive => {
        if (!incentive) {
          return of(null);
        }
        return from(this.indexedDbIncentiveService.clearDB()).pipe(
          tap(() => console.log('Incentive deleted from IndexedDB')),
          catchError(error => {
            console.error('Error deleting incentive from IndexedDB:', error);
            return of(null);
          }),
          map(() => incentive)
        );
      }),
      switchMap(incentive => {
        if (!incentive) {
          return of(null);
        }
        const todayDate = new Date().toISOString().split('T')[0];
        const startDate = incentive.startDate.split('T')[0];
        const endDate = incentive.endDate.split('T')[0];
        if (startDate > todayDate || endDate < todayDate) {
          return of(null);
        }
        return forkJoin({
          noticePageImg: this.imageService.convertToBase64(incentive.noticePageImg)
            .pipe(catchError(() => of('' as string))),
          winnerPageImg: this.imageService.convertToBase64(incentive.winnerPageImg)
            .pipe(catchError(() => of('' as string)))
        }).pipe(
          map(({ noticePageImg, winnerPageImg }) => ({
            ...incentive,
            noticePageImg: noticePageImg || INCENTIVE_IMG_DEFAULT,
            winnerPageImg: winnerPageImg || INCENTIVE_IMG_DEFAULT,
          }))
        );
      }),
      tap(incentiveData => {
        if (incentiveData) {
          this.addIncentiveToIndexedDb(incentiveData);
        }
      })
    ).subscribe();
  }

  private addIncentiveToIndexedDb(data: IIncentive): void {
    this.indexedDbIncentiveService.add(data).then(() => {
    });
  }

  protected readonly PollTypeEnum = PollTypeEnum;
}
